import { DependencyList, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isPlainObject from 'lodash/isPlainObject';
import set from 'lodash/set';
import useResourceLoader from 'src/util/useResourceLoader';

export type FieldErrors = Record<string, string>;

export type SaveOutcome<R> = { ok: boolean; result?: R };

export type UseEntityEditorOptions<T, R> = {
  load: () => T | Promise<T>;
  save: (draft: T) => Promise<R>;
  /** `original` is the last loaded or saved record, for rules that only apply to changed values. */
  validate?: (draft: T, original: T | undefined) => FieldErrors;
  deps: DependencyList;
};

export type EntityEditor<T, R> = {
  draft: T | undefined;
  original: T | undefined;
  loading: boolean;
  loadError: Error | undefined;
  isDirty: boolean;
  errors: FieldErrors;
  errorCount: number;
  submitted: boolean;
  errorFor: (path: string) => string | undefined;
  setField: (path: string, value: unknown) => void;
  clearTouched: (prefix: string) => void;
  update: (fn: (draft: T) => T) => void;
  saving: boolean;
  saveError: string | undefined;
  lastSavedAt: number | null;
  save: () => Promise<SaveOutcome<R>>;
};

const NO_ERRORS: FieldErrors = {};

function isBlank(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

/** Copy of a value with blank (undefined / null / '') object entries removed, recursively. */
function withoutBlanks(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(withoutBlanks);
  if (isPlainObject(value)) {
    const result: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, entry]) => {
      if (!isBlank(entry)) result[key] = withoutBlanks(entry);
    });
    return result;
  }
  return isBlank(value) ? null : value;
}

/** Deep equality that treats undefined, null, '' and a missing key as the same "empty" value. */
export function sameValue(a: unknown, b: unknown): boolean {
  return isEqual(withoutBlanks(a), withoutBlanks(b));
}

/**
 * The draft to keep when the user edited `current` while `posted` was being saved and the server
 * answered with `saved`: the user's edits win, and wherever they left the posted value alone the
 * server's copy is taken, so ids and stamps the save assigned (e.g. on new rows) are not lost.
 */
export function mergeConcurrentEdits(posted: unknown, saved: unknown, current: unknown): unknown {
  if (sameValue(current, posted)) return saved;
  if (Array.isArray(posted) && Array.isArray(saved) && Array.isArray(current)) {
    // Rows can only be lined up by position while the save kept the posted ones and the user only appended.
    if (saved.length !== posted.length || current.length < posted.length) return current;
    return current.map((row, index) => (index < posted.length ? mergeConcurrentEdits(posted[index], saved[index], row) : row));
  }
  if (isPlainObject(posted) && isPlainObject(saved) && isPlainObject(current)) {
    const postedRecord = posted as Record<string, unknown>;
    const savedRecord = saved as Record<string, unknown>;
    const merged: Record<string, unknown> = { ...savedRecord, ...(current as Record<string, unknown>) };
    Object.keys(merged).forEach(key => {
      merged[key] = mergeConcurrentEdits(postedRecord[key], savedRecord[key], merged[key]);
    });
    return merged;
  }
  return current;
}

/**
 * Editing lifecycle for one record. The draft is always a full copy of what was loaded, so `save`
 * posts every field — including the ones a screen never shows. Our backends map the whole posted DTO
 * onto the stored entity, so posting only edited fields would wipe the rest.
 */
export function useEntityEditor<T extends object, R = unknown>(options: UseEntityEditorOptions<T, R>): EntityEditor<T, R> {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [loaded, loadingFlag, loadError] = useResourceLoader<T>(() => Promise.resolve(optionsRef.current.load()), options.deps);
  const [original, setOriginal] = useState<T>();
  const [draft, setDraft] = useState<T>();
  const [touched, setTouched] = useState<Record<string, true>>({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  // The disabled Save button is not enough: a keyboard shortcut or a double call can start a second save.
  const savingRef = useRef(false);

  useEffect(() => {
    if (loaded === undefined) return;
    setOriginal(loaded);
    setDraft(cloneDeep(loaded));
    setTouched({});
    setSubmitted(false);
    setSaveError(undefined);
  }, [loaded]);

  const errors = useMemo(() => (draft && options.validate ? options.validate(draft, original) : NO_ERRORS), [draft, original, options.validate]);
  const errorCount = Object.keys(errors).length;
  const isDirty = useMemo(() => draft !== undefined && !sameValue(original, draft), [original, draft]);

  const errorFor = useCallback((path: string) => (submitted || touched[path] ? errors[path] : undefined), [errors, submitted, touched]);

  const setField = useCallback((path: string, value: unknown) => {
    setDraft(current => {
      if (current === undefined) return current;
      const next = cloneDeep(current);
      set(next, path, value);
      return next;
    });
    setTouched(current => (current[path] ? current : { ...current, [path]: true }));
  }, []);

  const clearTouched = useCallback((prefix: string) => {
    setTouched(current => {
      const kept = Object.keys(current).filter(path => !path.startsWith(prefix));
      return kept.length === Object.keys(current).length ? current : Object.fromEntries(kept.map(path => [path, true as const]));
    });
  }, []);

  const update = useCallback((fn: (current: T) => T) => {
    setDraft(current => (current === undefined ? current : fn(cloneDeep(current))));
  }, []);

  const save = useCallback(async (): Promise<SaveOutcome<R>> => {
    if (draft === undefined || savingRef.current) return { ok: false };
    setSubmitted(true);
    if (errorCount > 0) return { ok: false };

    savingRef.current = true;
    setSaving(true);
    setSaveError(undefined);
    try {
      const result = await optionsRef.current.save(draft);
      const next = (result && typeof result === 'object' ? result : draft) as T;
      setOriginal(next);
      // Edits made while the save was in flight are kept on top of the saved record (and stay dirty).
      setDraft(current =>
        current === draft || current === undefined ? cloneDeep(next) : (mergeConcurrentEdits(draft, cloneDeep(next), current) as T)
      );
      setTouched({});
      setSubmitted(false);
      setLastSavedAt(Date.now());
      return { ok: true, result };
    } catch (e) {
      setSaveError((e as Error)?.message || String(e));
      return { ok: false };
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [draft, errorCount]);

  return {
    draft,
    original,
    loading: !!loadingFlag || (draft === undefined && !loadError),
    loadError,
    isDirty,
    errors,
    errorCount,
    submitted,
    errorFor,
    setField,
    clearTouched,
    update,
    saving,
    saveError,
    lastSavedAt,
    save,
  };
}

export default useEntityEditor;
